import { Descope } from '@descope/react-sdk/flows';
import React, { useState } from 'react';
import { getOrCreateClientSessionId } from '@shared/utils/clientSessionId';
import { withClientSessionId } from '@shared/utils/descopeActionPayload';
import RequestPasswordResetScreen, {
  type RequestPasswordResetScreenMode,
} from './RequestPasswordResetScreen';
import {
  REQUEST_PASSWORD_RESET_FLOW_ID,
  REQUEST_PASSWORD_RESET_INTERACTION_IDS,
  RequestPasswordResetScreenNames,
} from './screenMapping';

const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;
type RawDescopeNext = (
  interactionId: string,
  payload: Record<string, unknown>
) => Promise<unknown>;

type FlowForm = Record<string, unknown>;
type DescopeContext = {
  error?: unknown;
  form?: FlowForm;
  [key: string]: unknown;
};
type FlowState = DescopeContext & {
  next?: DescopeNext;
  screenName?: string;
};

const getDescopeRedirectUrl = (): string => {
  const url = new URL(window.location.href);
  url.hash = '';
  return url.toString();
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const getErrorText = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  return typeof error.text === 'string' ? error.text : undefined;
};

const getErrorDetail = (error: unknown): unknown =>
  isRecord(error) && 'detail' in error ? error.detail : error;

const resetErrorState = (
  setState: React.Dispatch<React.SetStateAction<FlowState>>
) =>
  setState(prevState => ({
    ...prevState,
    error: {},
  }));

const getEmailOrUsernameFromForm = (form: FlowForm): string | undefined => {
  const emailOrUsername = form['form.emailOrUsername'];

  return typeof emailOrUsername === 'string' ? emailOrUsername : undefined;
};

const getScreenMode = (
  screenName?: string
): RequestPasswordResetScreenMode | undefined => {
  switch (screenName) {
    case RequestPasswordResetScreenNames.REQUEST:
      return 'form';
    case RequestPasswordResetScreenNames.SENT:
      return 'sent';
    case RequestPasswordResetScreenNames.TOO_SOON:
      return 'tooSoon';
    case RequestPasswordResetScreenNames.ERROR:
      return 'error';
    case RequestPasswordResetScreenNames.TECHNICAL_ERROR:
      return 'technicalError';
    default:
      return undefined;
  }
};

const RequestPasswordResetFlow: React.FC = () => {
  const [state, setState] = useState<FlowState>({ error: {} });
  const [form, setForm] = useState<FlowForm>({});
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [clientSessionId] = useState(getOrCreateClientSessionId);
  const flowForm = {
    ...(state?.form ?? {}),
    ...form,
  };
  const screenMode = getScreenMode(state?.screenName);

  const returnToLogin = async () => {
    if (state.next) {
      await state.next(
        REQUEST_PASSWORD_RESET_INTERACTION_IDS.BACK_TO_LOGIN,
        flowForm
      );
    }

    window.location.href = `${LINKTREE_BASE_URL}/login`;
  };

  const retryInteractionByScreen: Partial<
    Record<RequestPasswordResetScreenMode, string>
  > = {
    tooSoon: REQUEST_PASSWORD_RESET_INTERACTION_IDS.RETRY_TOO_SOON,
    error: REQUEST_PASSWORD_RESET_INTERACTION_IDS.RETRY,
    technicalError: REQUEST_PASSWORD_RESET_INTERACTION_IDS.RETRY_AFTER_ERROR,
  };

  return (
    <Descope
      flowId={REQUEST_PASSWORD_RESET_FLOW_ID}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId(flowForm)}
      onScreenUpdate={(
        screenName: string,
        context: DescopeContext,
        next: RawDescopeNext
      ) => {
        const nextWithClientSessionId: DescopeNext = (interactionId, payload) =>
          next(interactionId, withClientSessionId(payload ?? {}));

        setState(prevState => ({
          ...prevState,
          ...context,
          next: nextWithClientSessionId,
          screenName,
          error: context?.error || {},
        }));

        const customScreenNames = [
          RequestPasswordResetScreenNames.REQUEST,
          RequestPasswordResetScreenNames.SENT,
          RequestPasswordResetScreenNames.TOO_SOON,
          RequestPasswordResetScreenNames.ERROR,
          RequestPasswordResetScreenNames.TECHNICAL_ERROR,
        ];

        return customScreenNames.includes(
          screenName as RequestPasswordResetScreenNames
        );
      }}
      onSuccess={() => {
        window.location.href = `${LINKTREE_BASE_URL}/login`;
      }}
      onError={(error: unknown) => {
        setState(prevState => ({
          ...prevState,
          error: getErrorDetail(error),
        }));
      }}
    >
      {screenMode && (
        <RequestPasswordResetScreen
          screen={screenMode}
          initialEmailOrUsername={getEmailOrUsernameFromForm(flowForm)}
          onEmailOrUsernameChange={data =>
            setForm(prevForm => ({ ...prevForm, ...data }))
          }
          onSubmit={async (emailOrUsername: string) => {
            const nextForm = {
              ...flowForm,
              'form.emailOrUsername': emailOrUsername,
            };
            setForm(nextForm);

            if (state.next) {
              await state.next(
                REQUEST_PASSWORD_RESET_INTERACTION_IDS.SUBMIT,
                nextForm
              );
            }
          }}
          onRetry={async () => {
            const retryInteraction = retryInteractionByScreen[screenMode];

            if (state.next && retryInteraction) {
              await state.next(retryInteraction, flowForm);
            }
          }}
          onBackToLogin={returnToLogin}
          errorText={getErrorText(state.error)}
          resetError={() => resetErrorState(setState)}
        />
      )}
    </Descope>
  );
};

export default RequestPasswordResetFlow;
